import { useMemo, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { FaCaretUp, FaCaretDown } from 'react-icons/fa';
import MobileDiscoverySearchToolbar from './MobileDiscoverySearchToolbar';
import MobileAppliedChips from './MobileAppliedChips';
import MobileDiscoveryInsightCard from './MobileDiscoveryInsightCard';
import MobileLoadMoreFooter from './MobileLoadMoreFooter';
import DiscoveryEmptyState from './DiscoveryEmptyState';
import useMobileLoadMore from './useMobileLoadMore';
import { emptyInsightFilters, getMobileInsightResults, insightSortOptions } from './mobileInsightResults';
import icFilter from '../../assets/img/ic-filter.svg';
import icSort from '../../assets/img/ic-sort.svg';

const emptySort = { field: null, direction: 'asc' };

const MobileInsightList = ({ insights, cityId, countryId, searchQuery, onSearchChange, normalizeUrl }) => {
    const [filters, setFilters] = useState(emptyInsightFilters);
    const [sort, setSort] = useState(emptySort);
    const [draftFilters, setDraftFilters] = useState(emptyInsightFilters);
    const [draftSort, setDraftSort] = useState(emptySort);
    const [drawer, setDrawer] = useState(null);
    const services = useMemo(() => [...new Set(insights
        .map(insight => insight.service).filter(Boolean))].sort(), [insights]);
    const results = useMemo(() => getMobileInsightResults(insights, searchQuery, filters, sort),
        [insights, searchQuery, filters, sort]);
    const pagination = useMobileLoadMore({
        items: results,
        filterKey: JSON.stringify([cityId, countryId, searchQuery, filters, sort]),
        storageKey: 'discovery-city-insights-mobile',
        isMobile: true,
    });
    const openFilters = () => { setDraftFilters(filters); setDrawer('filter'); };
    const openSort = () => { setDraftSort(sort); setDrawer('sort'); };
    const closeDrawer = () => setDrawer(null);
    const chips = [];
    if (filters.service) chips.push({
        key: 'service', label: `Service: ${filters.service}`, onOpen: openFilters,
        onRemove: () => setFilters(current => ({ ...current, service: '' })),
    });
    if (sort.field) chips.push({
        key: 'sort', label: insightSortOptions.find(option => option.field === sort.field).label,
        icon: sort.direction === 'asc' ? <FaCaretUp aria-label="Ascending" /> : <FaCaretDown aria-label="Descending" />,
        onOpen: openSort, onRemove: () => setSort(emptySort), removeLabel: 'Clear insight sort',
    });
    const hasCriteria = Boolean(searchQuery.trim() || filters.service);
    const isFilter = drawer === 'filter';
    const clearDrawer = () => {
        if (isFilter) { setFilters(emptyInsightFilters); setDraftFilters(emptyInsightFilters); }
        else { setSort(emptySort); setDraftSort(emptySort); }
        closeDrawer();
    };
    const applyDrawer = () => {
        if (isFilter) setFilters(draftFilters);
        else setSort(draftSort);
        closeDrawer();
    };

    return (
        <>
            <MobileDiscoverySearchToolbar searchValue={searchQuery} onSearchChange={onSearchChange}
                searchPlaceholder="Search insights..." searchAriaLabel="Search insights"
                actions={[
                    { label: 'Filter insights', icon: icFilter, onClick: openFilters },
                    { label: 'Sort insights', icon: icSort, onClick: openSort },
                ]} />
            <MobileAppliedChips chips={chips} />
            {pagination.totalCount === 0 ? (
                <DiscoveryEmptyState
                    className="discovery-empty-state--mobile-jobs"
                    title={hasCriteria ? 'No matching city insights found' : 'No city insights found'}
                    description={hasCriteria ? 'Try changing your search or filters.' : 'City insights will be displayed here.'}
                    primaryAction={hasCriteria ? { label: 'Clear search & filters', onClick: () => {
                        onSearchChange(''); setFilters(emptyInsightFilters);
                    } } : undefined}
                    secondaryAction={hasCriteria ? { label: 'Adjust filters', onClick: openFilters } : undefined}
                />
            ) : (
                <div className="mobile-company-list mobile-insight-list">
                    {pagination.mobileItems.map(insight => (
                        <MobileDiscoveryInsightCard key={insight._id} insight={insight} normalizeUrl={normalizeUrl} />
                    ))}
                    <MobileLoadMoreFooter visibleCount={pagination.visibleCount} totalCount={pagination.totalCount}
                        itemLabel="insights" onLoadMore={pagination.loadMore}
                        isLoading={pagination.isLoadingMore} hasMore={pagination.hasMore} />
                </div>
            )}
            <Modal show={Boolean(drawer)} onHide={closeDrawer}
                className={`with-form discovery-filter-mobile-modal discovery-filter-mobile-modal--fullscreen${isFilter ? '' : ' discovery-sort-mobile-modal'}`}>
                <div className="discovery-filter-mobile-panel">
                    <div className="discovery-filter-mobile-panel__header">
                        <Modal.Title>{isFilter ? 'Filter insights' : 'Sort insights'}</Modal.Title>
                        <div className="discovery-filter-mobile-panel__header-actions">
                            <button type="button" className="discovery-filter-mobile-panel__close"
                                onClick={closeDrawer} aria-label="Close insight drawer">&times;</button>
                        </div>
                    </div>
                    <Modal.Body>
                        {isFilter ? (
                            <>
                                <Form.Group controlId="insight-service" className="mb-4">
                                    <Form.Label>Service</Form.Label>
                                    <Form.Select value={draftFilters.service}
                                        onChange={event => setDraftFilters(current => ({ ...current, service: event.target.value }))}>
                                        <option value="">All services</option>
                                        {services.map(value => <option key={value} value={value}>{value}</option>)}
                                    </Form.Select>
                                </Form.Group>

                            </>
                        ) : (
                            <div className="discovery-sort-mobile-options" role="radiogroup" aria-label="Sort insights by">
                                {insightSortOptions.map(option => {
                                    const active = draftSort.field === option.field;
                                    return (
                                        <button key={option.field} type="button" role="radio" aria-checked={active}
                                            className={`discovery-sort-mobile-option${active ? ' is-active' : ''}`}
                                            onClick={() => setDraftSort({ field: option.field,
                                                direction: active && draftSort.direction === 'asc' ? 'desc' : 'asc' })}>
                                            <span className="discovery-sort-mobile-option__label">
                                                <span className="discovery-sort-mobile-option__radio" aria-hidden="true" />
                                                <span>{option.label}</span>
                                            </span>
                                            {active && <span className="discovery-sort-mobile-option__direction">
                                                <span>{draftSort.direction === 'asc' ? 'Ascending' : 'Descending'}</span>
                                                {draftSort.direction === 'asc' ? <FaCaretUp aria-hidden="true" /> : <FaCaretDown aria-hidden="true" />}
                                            </span>}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </Modal.Body>
                    <div className="discovery-filter-mobile-panel__footer">
                        <Button variant="outline-secondary" className="discovery-filter-mobile-panel__reset" onClick={clearDrawer}>
                            {isFilter ? 'Clear all' : 'Clear sort'}
                        </Button>
                        <Button variant="primary" className="discovery-filter-mobile-panel__apply" onClick={applyDrawer}>
                            {isFilter ? 'Apply' : 'Apply sort'}
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default MobileInsightList;
